import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, PoundSterling, Home, Calendar, BarChart2, ArrowRight, Info } from 'lucide-react';

const ProfitCalculatorPage = () => {
  const [inputs, setInputs] = useState({
    propertyType: 'apartment',
    bedrooms: 2,
    city: 'London',
    monthlyRent: 2200,
    nightlyRate: 180,
    occupancyRate: 72,
    cleaningFee: 60,
    managementFee: 15,
    utilities: 250,
    otherCosts: 100,
  });

  const set = (key, val) => setInputs(p => ({ ...p, [key]: Number(val) || val }));

  // Calculations
  const avgNightsPerMonth = (inputs.occupancyRate / 100) * 30.4;
  const grossRevenue = (inputs.nightlyRate * avgNightsPerMonth) + (inputs.cleaningFee * Math.ceil(avgNightsPerMonth / 3));
  const managementCost = grossRevenue * (inputs.managementFee / 100);
  const platformFee = grossRevenue * 0.13;
  const totalCosts = inputs.monthlyRent + inputs.utilities + inputs.otherCosts + managementCost + platformFee;
  const netMonthly = grossRevenue - totalCosts;
  const netAnnual = netMonthly * 12;
  const roi = ((netAnnual / (inputs.monthlyRent * 12)) * 100).toFixed(1);
  const breakEven = totalCosts > 0 ? Math.ceil((totalCosts / inputs.nightlyRate)) : 0;

  const cities = ['London', 'Manchester', 'Edinburgh', 'Birmingham', 'Bristol', 'Liverpool', 'Leeds', 'Bath', 'Oxford', 'Cambridge'];

  const benchmarks = {
    London: { nightly: 185, occupancy: 74 },
    Manchester: { nightly: 115, occupancy: 71 },
    Edinburgh: { nightly: 140, occupancy: 76 },
    Birmingham: { nightly: 100, occupancy: 68 },
    Bristol: { nightly: 120, occupancy: 73 },
    Liverpool: { nightly: 95, occupancy: 70 },
    Leeds: { nightly: 105, occupancy: 69 },
    Bath: { nightly: 155, occupancy: 77 },
    Oxford: { nightly: 160, occupancy: 75 },
    Cambridge: { nightly: 165, occupancy: 74 },
  };

  const applyBenchmark = (city) => {
    const b = benchmarks[city];
    if (b) setInputs(p => ({ ...p, city, nightlyRate: b.nightly, occupancyRate: b.occupancy }));
    else setInputs(p => ({ ...p, city }));
  };

  const fmt = (n) => `£${Math.max(0, n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div></Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Browse</Link>
            <Link to="/become-host" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Become a Host</Link>
            <Link to="/login" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#1F3A68] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp size={32} />
            <h1 className="text-4xl font-bold">STR Profit Calculator</h1>
          </div>
          <p className="text-white/80 text-lg max-w-2xl">
            Estimate your monthly and annual profit from a short-term rental. Adjust the inputs to model different scenarios for your property.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Inputs */}
          <div className="lg:col-span-3 space-y-6">
            {/* Property */}
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="flex items-center gap-2 text-[#1F3A68] font-bold text-lg mb-5">
                <Home size={20} />
                Property Details
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Property type</label>
                  <select value={inputs.propertyType} onChange={e => set('propertyType', e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]">
                    <option value="room">Private Room</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="cottage">Cottage / Rural</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Bedrooms</label>
                  <select value={inputs.bedrooms} onChange={e => set('bedrooms', e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]">
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} bedroom{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">City</label>
                  <select value={inputs.city} onChange={e => applyBenchmark(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]">
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Info size={11} />
                    Selecting a city auto-fills market benchmarks for that area
                  </p>
                </div>
              </div>
            </div>

            {/* Revenue */}
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="flex items-center gap-2 text-[#1F3A68] font-bold text-lg mb-5">
                <PoundSterling size={20} />
                Revenue Inputs
              </div>
              <div className="space-y-5">
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Average nightly rate</label>
                    <span className="text-sm font-bold text-[#1F3A68]">£{inputs.nightlyRate}</span>
                  </div>
                  <input type="range" min="30" max="1000" step="5" value={inputs.nightlyRate}
                    onChange={e => set('nightlyRate', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>£30</span><span>£1,000</span></div>
                </div>
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Target occupancy rate</label>
                    <span className="text-sm font-bold text-[#1F3A68]">{inputs.occupancyRate}%</span>
                  </div>
                  <input type="range" min="20" max="100" step="1" value={inputs.occupancyRate}
                    onChange={e => set('occupancyRate', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>20%</span><span>100%</span></div>
                </div>
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Cleaning fee per stay</label>
                    <span className="text-sm font-bold text-[#1F3A68]">£{inputs.cleaningFee}</span>
                  </div>
                  <input type="range" min="0" max="300" step="5" value={inputs.cleaningFee}
                    onChange={e => set('cleaningFee', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>£0</span><span>£300</span></div>
                </div>
              </div>
            </div>

            {/* Costs */}
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="flex items-center gap-2 text-[#1F3A68] font-bold text-lg mb-5">
                <Calendar size={20} />
                Monthly Costs
              </div>
              <div className="space-y-5">
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Monthly rent / mortgage</label>
                    <span className="text-sm font-bold text-[#1F3A68]">£{inputs.monthlyRent.toLocaleString()}</span>
                  </div>
                  <input type="range" min="500" max="10000" step="50" value={inputs.monthlyRent}
                    onChange={e => set('monthlyRent', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>£500</span><span>£10,000</span></div>
                </div>
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Management fee</label>
                    <span className="text-sm font-bold text-[#1F3A68]">{inputs.managementFee}%</span>
                  </div>
                  <input type="range" min="0" max="30" step="1" value={inputs.managementFee}
                    onChange={e => set('managementFee', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                  <p className="text-xs text-gray-400 mt-1">Set to 0% if self-managing</p>
                </div>
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Utilities (bills, internet)</label>
                    <span className="text-sm font-bold text-[#1F3A68]">£{inputs.utilities}</span>
                  </div>
                  <input type="range" min="0" max="1000" step="25" value={inputs.utilities}
                    onChange={e => set('utilities', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                </div>
                <div>
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-semibold text-gray-700">Other costs (insurance, supplies)</label>
                    <span className="text-sm font-bold text-[#1F3A68]">£{inputs.otherCosts}</span>
                  </div>
                  <input type="range" min="0" max="500" step="25" value={inputs.otherCosts}
                    onChange={e => set('otherCosts', e.target.value)}
                    className="w-full accent-[#1F3A68]" />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 space-y-5">
            {/* Main result */}
            <div className={`rounded-2xl p-7 text-white ${netMonthly >= 0 ? 'bg-gradient-to-br from-[#1F3A68] to-[#2D5AA0]' : 'bg-gradient-to-br from-[#CB3B3B] to-[#a32d2d]'}`}>
              <div className="text-white/70 text-sm font-semibold mb-1">Estimated monthly profit</div>
              <div className="text-5xl font-black mb-1">{fmt(netMonthly)}</div>
              <div className="text-white/60 text-sm">per month</div>
              <div className="border-t border-white/20 mt-6 pt-5 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-white/60 text-xs mb-0.5">Annual profit</div>
                  <div className="text-2xl font-bold">{fmt(netAnnual)}</div>
                </div>
                <div>
                  <div className="text-white/60 text-xs mb-0.5">Return on rent</div>
                  <div className="text-2xl font-bold">{roi}%</div>
                </div>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-white rounded-2xl p-7 shadow-sm">
              <div className="flex items-center gap-2 text-[#1F3A68] font-bold mb-5">
                <BarChart2 size={18} />
                Monthly breakdown
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Rental income</span>
                  <span className="font-semibold text-green-600">+{fmt(inputs.nightlyRate * avgNightsPerMonth)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cleaning fees collected</span>
                  <span className="font-semibold text-green-600">+{fmt(inputs.cleaningFee * Math.ceil(avgNightsPerMonth / 3))}</span>
                </div>
                <div className="border-t pt-3 flex justify-between text-sm font-semibold">
                  <span>Gross revenue</span>
                  <span className="text-green-700">+{fmt(grossRevenue)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Rent / mortgage</span>
                  <span>−{fmt(inputs.monthlyRent)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Utilities</span>
                  <span>−{fmt(inputs.utilities)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Management ({inputs.managementFee}%)</span>
                  <span>−{fmt(managementCost)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Platform fee (13%)</span>
                  <span>−{fmt(platformFee)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Other costs</span>
                  <span>−{fmt(inputs.otherCosts)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-bold text-base">
                  <span>Net profit</span>
                  <span className={netMonthly >= 0 ? 'text-green-700' : 'text-red-600'}>{netMonthly >= 0 ? '+' : ''}{fmt(netMonthly)}</span>
                </div>
              </div>
            </div>

            {/* Key metrics */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-[#1F3A68] mb-4">Key metrics</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg nights booked/month</span>
                  <span className="font-semibold">{avgNightsPerMonth.toFixed(1)} nights</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Break-even nights/month</span>
                  <span className="font-semibold">{breakEven} nights</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Market benchmark ({inputs.city})</span>
                  <span className="font-semibold">£{benchmarks[inputs.city]?.nightly || '—'}/night · {benchmarks[inputs.city]?.occupancy || '—'}% occ.</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-[#1F3A68]/5 border border-[#1F3A68]/10 rounded-2xl p-6 text-center">
              <p className="text-[#1F3A68] font-semibold mb-3">Ready to start earning?</p>
              <Link to="/become-host" className="flex items-center justify-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#1F3A68]/90 transition-colors w-full">
                List Your Property
                <ArrowRight size={16} />
              </Link>
              <Link to="/host/resources" className="block mt-2 text-sm text-[#1F3A68] hover:underline">
                Read our host guides →
              </Link>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-gray-400 text-xs mt-8 text-center max-w-2xl mx-auto">
          This calculator provides estimates for illustrative purposes only. Actual results will vary based on property condition, location, guest reviews, seasonality, and market conditions. Always conduct independent research before making investment decisions.
        </p>
      </div>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-[23px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[23px] w-auto object-contain brightness-0 invert" /></div>
          <div className="flex gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/careers" className="hover:text-white">Careers</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ariv. A product of Brick & Capital Ltd.</p>
          <p className="text-white/40 text-xs mt-2"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/70">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
        </div>
      </footer>
    </div>
  );
};

export default ProfitCalculatorPage;
